import { useEffect, useState } from "react";
import { Mail, RefreshCw, ShieldAlert, CornerDownRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/utils2";

export default function EmailLogs() {
  const [logs, setLogs] = useState([]);
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(null);

  const load = async () => {
    const [{ data: l }, { data: c }] = await Promise.all([
      api.get("/email-logs"),
      api.get("/admin/email-config"),
    ]);
    setLogs(l); setConfig(c);
  };
  useEffect(() => { load(); }, []);

  const override = config?.email_test_override;
  const mode = config?.email_mode;

  return (
    <div className="space-y-3">
      {/* TEST MODE banner */}
      {override && (
        <div
          data-testid="email-test-mode-banner"
          className="rounded-lg border border-[#8A6E1F] bg-[#1F1808] text-[#FCD34D] px-4 py-3 flex items-start gap-3"
        >
          <ShieldAlert className="w-5 h-5 mt-0.5 shrink-0"/>
          <div className="text-sm leading-relaxed">
            <div className="font-semibold uppercase tracking-[0.08em] text-[11px] mb-0.5">Test mode</div>
            <div>
              All emails are redirected to{" "}
              <span className="font-mono font-semibold" data-testid="email-test-mode-target">{override}</span>.
              The original intended recipient is still recorded on each row below. Configure via the{" "}
              <span className="font-mono">EMAIL_TEST_OVERRIDE</span> environment variable; remove it (or set it to an empty string) to deliver to real recipients.
            </div>
          </div>
        </div>
      )}

      {!override && mode === "mock" && (
        <div className="rounded-lg border border-border bg-[#141414] text-muted-foreground px-4 py-2 text-sm" data-testid="email-mock-banner">
          <strong>MOCK mode:</strong> emails are logged here but not actually delivered. Set <span className="font-mono">EMAIL_MODE=live</span> to send real emails via Resend.
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold">Email log</h3>
        <Button data-testid="emails-refresh-btn" size="sm" variant="outline" onClick={load} className="gap-1.5"><RefreshCw className="w-3.5 h-3.5"/> Refresh</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="border border-border rounded-lg bg-[#1E1E1E] max-h-[700px] overflow-y-auto">
          {logs.length === 0 ? (
            <div className="text-sm text-muted-foreground p-8 text-center">No emails sent yet.</div>
          ) : logs.map((l) => {
            const redirected = override && (l.error || "").startsWith("Redirected to");
            return (
              <button
                key={l.id}
                data-testid={`email-log-${l.id}`}
                onClick={() => setOpen(l)}
                className={`w-full text-left p-3 border-b border-border last:border-b-0 hover:bg-[#141414] ${open?.id===l.id ? "bg-accent/40" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm truncate">{l.subject}</div>
                  <StatusPill status={l.status}/>
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex justify-between gap-2">
                  <span className="truncate" data-testid={`email-log-to-${l.id}`}>
                    <span className="text-muted-foreground/70">To:</span> {l.to}
                  </span>
                  <span className="whitespace-nowrap">{fmtDateTime(l.sent_at)}</span>
                </div>
                {redirected && (
                  <div
                    data-testid={`email-log-redirected-${l.id}`}
                    className="mt-1 text-[11px] text-[#FCD34D] bg-[#1F1808] border border-[#8A6E1F] rounded px-1.5 py-0.5 inline-flex items-center gap-1"
                  >
                    <CornerDownRight className="w-3 h-3"/>
                    Delivered to <span className="font-mono">{override}</span> (test override)
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="border border-border rounded-lg bg-[#1E1E1E] p-4 min-h-[400px]">
          {open ? (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1"><Mail className="w-4 h-4"/> Preview</div>
              <div className="text-xs space-y-1 font-mono mb-3 border-b border-border pb-3">
                <div><span className="text-muted-foreground">From:</span> {config?.email_from || "—"}</div>
                <div>
                  <span className="text-muted-foreground">Intended recipient:</span> {open.to}
                </div>
                {override && (open.error || "").startsWith("Redirected to") && (
                  <div className="text-[#FCD34D]">
                    <span className="text-muted-foreground">Delivered to (test override):</span> {override}
                  </div>
                )}
                <div><span className="text-muted-foreground">Subject:</span> {open.subject}</div>
                <div><span className="text-muted-foreground">Sent:</span> {fmtDateTime(open.sent_at)}</div>
                <div><span className="text-muted-foreground">Status:</span> {open.status}</div>
              </div>
              <iframe
                data-testid="email-preview-iframe"
                title="email-preview"
                srcDoc={open.body_html}
                className="w-full h-[500px] border border-border rounded-md bg-[#1E1E1E]"
              />
            </>
          ) : (
            <div className="text-sm text-muted-foreground h-full flex items-center justify-center">Select an email to preview</div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    sent: "bg-[#0F1F14] text-[#34D399] border border-[#1F5E3D]",
    mocked: "bg-[#1F1808] text-[#FCD34D] border border-[#8A6E1F]",
    failed: "bg-[#1F0A0A] text-[#F87171] border border-[#5E1F1F]",
  };
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${map[status] || ""}`}>{status}</span>;
}
